import { useNavigate, useLocation, NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  HeartPulse,
  Folder,
  AppWindow,
  FileBarChart,
  UserCog,
  LogOut,
  LucideIcon,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import logo from "@/assets/logo_header.png";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";

function Item({
  to,
  icon: Icon,
  children,
  exact = false,
}: {
  to: string;
  icon: LucideIcon;
  children: string;
  exact?: boolean;
}) {
  const { pathname } = useLocation();
  const isActive = exact ? pathname === to : pathname === to || pathname.startsWith(to + "/");

  const getNavCls = ({ isActive: navIsActive }: { isActive: boolean }) =>
    navIsActive ? "bg-primary/10 text-primary font-medium border-l-2 border-primary" : "hover:bg-muted/50";

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={children} isActive={isActive}>
        <NavLink to={to} end={exact} className={getNavCls}>
          <Icon className="h-4 w-4" />
          <span>{children}</span>
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function PacienteSidebar() {
  const nav = useNavigate();
  const { state, toggleSidebar } = useSidebar();
  const [loggingOut, setLoggingOut] = useState(false);
  const isCollapsed = state === "collapsed";
  const paddingClass = isCollapsed ? "p-[22px]" : "p-[16px]";

  async function logout() {
    try {
      setLoggingOut(true);
      await supabase.auth.signOut();
      nav("/auth");
    } finally {
      setLoggingOut(false);
    }
  }

  return (
    <Sidebar collapsible="icon" className="border-r bg-white">
      <SidebarRail />
      <div className={`border-b h-[73px] flex items-center gap-3 group-data-[collapsible=icon]:justify-center ${paddingClass} relative`}>
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 h-7 w-7 hover:bg-muted group-data-[collapsible=icon]:relative group-data-[collapsible=icon]:right-auto group-data-[collapsible=icon]:left-auto group-data-[collapsible=icon]:mx-auto"
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          {state === "collapsed" ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
        <img src={logo} alt="Elinia" className="h-auto w-auto group-data-[collapsible=icon]:hidden" />
      </div>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <Item to="/paciente" icon={LayoutDashboard} exact>
                Inicio
              </Item>
              <Item to="/paciente/minha-saude" icon={HeartPulse}>
                Minha Saúde
              </Item>
              <Item to="/paciente/categorias" icon={Folder}>
                Categorias
              </Item>
              <Item to="/paciente/aplicativos" icon={AppWindow}>
                Aplicativos
              </Item>
              <Item to="/paciente/relatorios" icon={FileBarChart}>
                Relatórios
              </Item>
              <SidebarSeparator className="my-1" />
              <Item to="/paciente/minha-conta" icon={UserCog}>
                Minha conta
              </Item>
              <Item to="/paciente/suporte" icon={HelpCircle}>
                Suporte
              </Item>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <div className="border-t" />
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={logout}
              disabled={loggingOut}
              tooltip="Sair da conta"
              className="hover:bg-destructive/10 hover:text-destructive"
            >
              <LogOut className="h-4 w-4" />
              <span>{loggingOut ? "Saindo..." : "Sair"}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
